<template>
  <div class="course-curriculum">
    <h3 class="course-curriculum__title">{{ t('courseDetails.curriculum.title') }}</h3>
    <span v-if="overview"
          class="course-curriculum__overview">{{ overview }}</span>
    <div class="course-curriculum__cards">
      <template v-for="(section, sectionIdx) in sections"
                :key="sectionIdx">
        <!-- <h4 v-if="section.title" class="course-curriculum__section-title">{{ section.title }}</h4> -->
        <div v-for="(item, itemIdx) in section.sections"
             :key="`${sectionIdx}-${itemIdx}`"
             class="course-curriculum__card">
          <span class="course-curriculum__card-icon"
                aria-hidden="true">
            <svg width="14"
                 height="17"
                 viewBox="0 0 14 17"
                 fill="none"
                 xmlns="http://www.w3.org/2000/svg"
                 class="course-curriculum__lesson-icon">
              <path d="M13.5001 16.2273H13.091V13.8434C13.2616 13.7866 13.418 13.694 13.5497 13.5696C13.7815 13.3506 13.9092 13.0596 13.9092 12.75L13.9091 1.15909C13.9091 0.519961 13.3585 0 12.6818 0H2.04546C1.4991 0 0.98543 0.200945 0.599098 0.565814C0.212766 0.930684 0 1.41581 0 1.93182V15.0681C0 15.5842 0.212766 16.0693 0.599098 16.4342C0.98543 16.7991 1.4991 17 2.04546 17H13.5001C13.7261 17 13.9092 16.827 13.9092 16.6136C13.9092 16.4003 13.7261 16.2273 13.5001 16.2273ZM0.818192 1.93182C0.818192 1.2927 1.36874 0.772736 2.04546 0.772736H12.6818C12.9074 0.772736 13.0909 0.946057 13.0909 1.15909L13.091 12.7499C13.091 12.8531 13.0485 12.9501 12.9712 13.0231C12.8939 13.0961 12.7912 13.1363 12.6819 13.1363H2.04546C1.59687 13.1363 1.17032 13.2718 0.818192 13.5222V1.93182ZM0.818192 15.0682C0.818192 14.429 1.36874 13.9091 2.04546 13.9091H12.2728V16.2273H2.04546C1.36874 16.2273 0.818192 15.7073 0.818192 15.0682Z"
                    fill="currentColor" />
              <path d="M3.68252 7.7268H10.2281C10.454 7.7268 10.6372 7.55382 10.6372 7.34045C10.6372 7.12709 10.454 6.9541 10.2281 6.9541H3.68252C3.45657 6.9541 3.27344 7.12709 3.27344 7.34045C3.27344 7.55382 3.45657 7.7268 3.68252 7.7268Z"
                    fill="currentColor" />
              <path d="M3.68252 5.40845H10.2281C10.454 5.40845 10.6372 5.23546 10.6372 5.02209C10.6372 4.8087 10.454 4.63574 10.2281 4.63574H3.68252C3.45657 4.63574 3.27344 4.80873 3.27344 5.02209C3.2734 5.23546 3.45657 5.40845 3.68252 5.40845Z"
                    fill="currentColor" />
            </svg>
          </span>
          <span class="course-curriculum__card-title">
            {{ sectionIdx + 1 }}. {{ item.title || item.summary }}
          </span>
          <span v-if="item.duration"
                class="course-curriculum__card-duration">{{ item.duration }}</span>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { CurriculumSection } from '@/domain/entities/curriculum.entity'

export type { CurriculumSection }

const { t } = useI18n()

defineProps<{ sections: CurriculumSection[]; overview?: string }>()
</script>

<style scoped>
.course-curriculum {
  margin-bottom: 40px;
}

.course-curriculum__title {
  font-family: var(--font-inter);
  font-weight: 600;
  font-size: 26px;
  line-height: 1.3;
  color: #000000;
  margin: 0 0 15px 0;
}

.course-curriculum__overview {
  display: block;
  font-family: var(--font-inter);
  font-size: 16px;
  color: #474b52;
  margin-bottom: 20px;
}

.course-curriculum__cards {
  display: flex;
  flex-direction: column;
  gap: 0;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  overflow: hidden;
}

.course-curriculum__section-title {
  font-family: var(--font-inter);
  font-weight: 600;
  font-size: 18px;
  line-height: 1.3;
  color: #252840;
  margin: 0 0 8px 0;
  padding-top: 8px;
}

.course-curriculum__section-title:first-child {
  padding-top: 0;
}

.course-curriculum__card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 65px;
  height: 65px;
  padding: 0 16px;
  background-color: transparent;
  border-bottom: 1px solid #e5e7eb;
  box-sizing: border-box;
  transition: background-color 0.2s;
}

.course-curriculum__card:last-child {
  border-bottom: none;
}

.course-curriculum__card:hover {
  background-color: #f8f9fa;
}

.course-curriculum__card-icon {
  display: inline-flex;
  flex-shrink: 0;
}

.course-curriculum__lesson-icon {
  width: 14px;
  height: 17px;
  color: var(--color-teal, #00CE9B);
}

.course-curriculum__card-title {
  flex: 1;
  font-family: var(--font-inter);
  font-size: 16px;
  font-weight: 600;
  color: #252840;
  padding-right: 16px;
  min-width: 0;
}

.course-curriculum__card-duration {
  font-family: var(--font-inter);
  font-size: 16px;
  font-weight: 400;
  color: #252840;
  flex-shrink: 0;
}

@media (max-width: 767px) {
  .course-curriculum__title {
    font-size: 22px;
  }
}

@media (max-width: 575px) {
  .course-curriculum__title {
    font-size: 20px;
  }

  .course-curriculum__card {
    padding: 0 16px;
    min-height: 65px;
    font-size: 14px;
  }

  .course-curriculum__card-title {
    font-size: 14px;
  }

  .course-curriculum__card-duration {
    font-size: 14px;
  }
}
</style>
